// A page this sign-in's role does not open, met by a deep link.
//
// THE RAIL DOES NOT OFFER IT, AND THE ROUTE DOES NOT DRAW IT EITHER (12 Oct).
// The console used to hide a rail item and then draw the screen anyway for a
// deep link, so the service's refusal arrived under the screen's own title,
// its filters and its Add buttons: a page the reader could see and could not
// use. roleAccess.ts is now the one statement of what each role opens, and
// check.mjs ties it to the service's guards, which answers the old objection
// that a second copy of the rule would drift. So a route the map closes draws
// this instead, and asks the service nothing.
//
// NOT `Refusal`, for Unverified.tsx's reason: that component draws a Failure,
// which is a decision the service made about a request that was sent. Nothing
// was sent here. No enrolment offer either: an authenticator does not change
// what a role opens.

import type { ReactNode } from "react";
import { NoticeBox } from "@nova/desk-ui/parts";
import { roleName, type Capability } from "./roleAccess.js";

/** The page's own title, the one its head and the rail give it. */
const TITLES: Readonly<Record<Capability, string>> = {
  activations: "Activations",
  addActivation: "Add activation",
  addClient: "New client",
  clients: "Clients",
  clientTheme: "Client theme",
  dashboard: "Dashboard",
  siteVisits: "Site visits",
  registrationSetups: "Registration setups",
  details: "Show details",
  pauseShow: "Pause this show",
  personActions: "Person actions",
  themes: "Themes",
  data: "Data",
  people: "People",
  unverified: "Unverified emails",
  draw: "Draw",
  activity: "Event activity",
  registration: "Registration",
  addContact: "Add a client contact",
  registrationTheme: "Registration · theme",
  registrationEmails: "Registration · emails",
  wheel: "Wheel",
  wheelPrizes: "Wheel · prizes",
  wheelResults: "Wheel · results",
  wheelCollect: "Mark collected",
  wheelExport: "Wheel · export",
  kiosk: "Kiosk",
  kioskScreens: "Kiosk · screens",
  kioskDevices: "Kiosk · devices",
  kioskTheme: "Kiosk · theme",
  game: "Game",
  board: "Board",
  boardEdit: "Board · save",
  boardTheme: "Board · theme",
  arcadeBoard: "Arcade board",
  gameSponsor: "Sponsor",
  settings: "Settings",
  emailStats: "Sending",
  team: "Team",
};

export function pageTitle(capability: Capability): string {
  return TITLES[capability];
}

export type NotForRoleProps = {
  /** The page's own title, so the reader knows what they followed a link to. */
  readonly title: string;
  readonly role: string;
  /** Where to go instead: the activations list, or this show's dashboard. */
  readonly onLeave: () => void;
  readonly leaveLabel: string;
};

export function NotForRole({ title, role, onLeave, leaveLabel }: NotForRoleProps): ReactNode {
  const who = roleName(role);
  return (
    <div className="stack not-for-role">
      {/* The page's name and nothing a read would carry: no freshness line
          and no Refresh, because nothing was read. NotThisSignIn's head. */}
      <header className="head">
        <h1>{title}</h1>
      </header>
      <NoticeBox kind="quiet" title="Not part of your access">
        <p>
          {who === null
            ? "This sign-in's role does not open this page."
            : `${who} sign-ins do not open this page.`}{" "}
          Nothing was loaded. Nova manages roles.
        </p>
      </NoticeBox>
      <div className="acts acts-flush">
        <button type="button" className="btn btn-quiet" onClick={onLeave}>
          {leaveLabel}
        </button>
      </div>
    </div>
  );
}
